import React, { useState } from 'react';
import { 
  User, UserPlus, ShoppingBag, Ticket, Gift, Clock, Heart,
  CreditCard, Bell, History, Star, ChevronRight, Check, Loader2
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

// Google Icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="mr-2">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const GuestCheckoutPrompt = ({ isOpen, onSignIn, onCreateAccount, onContinueAsGuest }) => {
  const [showBenefits, setShowBenefits] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  if (!isOpen) return null;

  // Google Sign In Handler
  const handleGoogleSignIn = () => {
    setIsGoogleLoading(true);
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const accountBenefits = [
    { 
      icon: History, 
      title: 'Order History', 
      description: 'Track all your orders and easily reorder favorites',
      highlight: true
    },
    { 
      icon: Bell, 
      title: 'Real-Time Updates', 
      description: 'Get notifications on order status and delivery tracking'
    },
    { 
      icon: Heart, 
      title: 'Save Favorites', 
      description: 'Save products and addresses for faster checkout'
    },
    { 
      icon: Gift, 
      title: 'Exclusive Offers', 
      description: 'Members-only discounts and early access to sales'
    },
    { 
      icon: CreditCard, 
      title: 'Saved Payment Methods', 
      description: 'Securely save cards for one-click checkout'
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      <Card className="relative z-10 w-full max-w-xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 text-white relative overflow-hidden">          
          <div className="relative">
            <div className="flex items-center justify-center gap-3 mb-2">
              <ShoppingBag className="w-8 h-8" />
              <h2 className="text-2xl font-bold">Ready to Checkout?</h2>
            </div>
            <p className="text-center text-white/80">
              Sign in for the best experience or continue as guest
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Main Options */}
          <div className="space-y-3 mb-6">
            {/* Sign In Button */}
            <Button
              onClick={onSignIn}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold"
              data-testid="sign-in-btn"
            >
              <User className="w-5 h-5 mr-2" />
              Sign In to My Account
            </Button>

            {/* Google Sign In Button */}
            <Button
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading}
              variant="outline"
              className="w-full h-14 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 text-lg font-semibold flex items-center justify-center"
              data-testid="google-signin-checkout-btn"
            >
              {isGoogleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <GoogleIcon />
              )}
              {isGoogleLoading ? 'Redirecting...' : 'Continue with Google'}
            </Button>

            {/* Create Account Button */}
            <Button
              onClick={onCreateAccount}
              variant="outline"
              className="w-full h-14 border-2 border-green-500 text-green-600 hover:bg-green-50 text-lg font-semibold"
              data-testid="create-account-btn"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Create New Account
              <Badge className="ml-2 bg-green-100 text-green-700 text-xs">FREE</Badge>
            </Button>

            {/* Divider */}
            <div className="flex items-center gap-4 my-4">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-sm text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Continue as Guest */}
            <Button
              onClick={onContinueAsGuest}
              variant="ghost"
              className="w-full h-12 text-gray-600 hover:bg-gray-100"
              data-testid="guest-checkout-btn"
            >
              Continue as Guest
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Benefits Toggle */}
          <button
            onClick={() => setShowBenefits(!showBenefits)}
            className="w-full flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700 font-medium py-2 mb-4"
          >
            <Star className="w-4 h-4" />
            {showBenefits ? 'Hide' : 'See'} Account Benefits
            <ChevronRight className={`w-4 h-4 transition-transform ${showBenefits ? 'rotate-90' : ''}`} />
          </button>

          {/* Benefits Section */}
          {showBenefits && (
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-200">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-500" />
                Why Create an Account?
              </h3>
              <div className="grid gap-3">
                {accountBenefits.map((benefit, idx) => {
                  const Icon = benefit.icon;
                  return (
                    <div 
                      key={idx}
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        benefit.highlight 
                          ? 'bg-gradient-to-r from-red-50 to-orange-50 border border-red-200' 
                          : 'bg-white/70'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        benefit.highlight ? 'bg-red-500' : 'bg-blue-500'
                      }`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className={`font-semibold ${benefit.highlight ? 'text-red-700' : 'text-gray-800'}`}>
                          {benefit.title}
                          {benefit.highlight && (
                            <Badge className="ml-2 bg-red-100 text-red-700 text-[10px]">HOT!</Badge>
                          )}
                        </p>
                        <p className="text-sm text-gray-600">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Guest Notice */}
          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-700 flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Guest checkout:</strong> You can still place orders, but you&apos;ll miss out on 
                raffle tickets, order tracking, and other member benefits.
              </span>
            </p>
          </div>
        </div>

        {/* Quick Benefits Bar */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3 text-green-500" /> Free to join
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3 text-green-500" /> No spam
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3 text-green-500" /> Secure checkout
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GuestCheckoutPrompt;
